import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSiteData } from '../context/DataContext.jsx'
import { useChatContext } from '../context/ChatContext.jsx'
import RailBlock from '../components/RailBlock.jsx'
import SideRow from '../components/SideRow.jsx'
import AboutSiteTrigger from '../components/AboutSiteTrigger.jsx'
import ErrorFallback from '../components/ErrorFallback.jsx'
import Skeleton from '../components/Skeleton.jsx'
import mugunthImg from '../assets/mugunth.jpeg'

function HomePage() {
  const { openChat } = useChatContext()
  const { data: siteData, loading, error, refetch } = useSiteData()
  const [bio, setBio] = useState(null)
  const [latestPost, setLatestPost] = useState(null)

  useEffect(() => {
    if (siteData) {
      setBio(siteData.bio?.[0] || null)
      setLatestPost(siteData.blogPosts?.[0] || null)
    }
  }, [siteData])

  if (loading) {
    return (
      <div className="pg-layout">
        <div>
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 pb-8">
              <Skeleton className="w-[180px] h-[220px] sm:w-[220px] sm:h-[268px] shrink-0" />
            <div className="flex-1 space-y-4">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-16 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-10 w-48 mt-4" />
            </div>
          </div>
          <Skeleton className="h-20 w-full mt-6" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (error) return <ErrorFallback message="Failed to load page data" onRetry={refetch} />
  if (!bio) return <ErrorFallback message="No bio found" />

  return (
    <div className="pg-layout">
      {/* LEFT primary */}
      <div>
        {/* Hero row */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 pb-8">
          {/* Photo */}
          <div className="shrink-0 w-[180px] sm:w-[220px] h-[220px] sm:h-[268px] overflow-hidden border border-soft-rule">
          {/*  <img*/}
          {/*      src={mugunthImg}*/}
          {/*      alt="Portrait of Mugunth J. Chandirasekaran"*/}
          {/*      className="w-full h-full object-cover scale-110"*/}
          {/*  />*/}
            {bio.photo_url ? (
              <img
                src={mugunthImg}
                alt="Portrait of Mugunth J. Chandirasekaran"
                  className="w-[180px] sm:w-[220px] h-[220px] sm:h-[268px] object-cover border border-soft-rule"
              />
            ) : (
              <div className="w-[180px] sm:w-[220px] h-[220px] sm:h-[268px] border border-soft-rule bg-soft-cream flex items-center justify-center relative">
                <span className="text-[56px] font-normal text-soft-accent tracking-[-1px]">MJC</span>
                <span className="absolute bottom-3 left-4 font-mono text-[9.5px] text-soft-accent-dim">mjc.portrait</span>
              </div>
            )}
          </div>

          {/* Text column */}
          <div className="flex-1 pt-1">
            {/*<p className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint mb-3">*/}
            {/*  Mugunth J. Chandirasekaran · Dublin*/}
            {/*</p>*/}
            <h1 className="text-[36px] sm:text-[48px] md:text-[64px] font-normal leading-[1] tracking-[-2.4px] mb-4">
              Mugunth J. Chandirasekaran.
            </h1>
            <p className="text-[22px] leading-[1.3] text-soft-ink mb-4">
              {bio.tagline || 'AI Engineer | Software Engineer'}
            </p>
            <p className="text-[17px] leading-[1.65] text-soft-dim max-w-[720px] mb-6">
              {bio.summary}
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={openChat}
                className="inline-flex items-center px-5 py-2.5 bg-soft-ink text-soft-bg text-sm cursor-pointer hover:opacity-90 transition-opacity"
              >
                Ask my agent →
              </button>
              <Link
                to="/blog"
                className="text-sm text-soft-dim hover:text-soft-ink transition-colors"
              >
                Read the writing
              </Link>
            </div>
          </div>
        </div>

        {/* Facts strip */}
        <div className="py-6 border-t border-soft-rule">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10">
            <div>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-soft-faint mb-1">Now</p>
              <p className="text-[15px] text-soft-ink">{bio.tagline || 'Finishing M.Sc. in AI at DCU'}</p>
            </div>
            <div>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-soft-faint mb-1">Recently</p>
              <p className="text-[15px] text-soft-ink">Software Analyst at Bounteous (2023–2025)</p>
            </div>
            <div>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-soft-faint mb-1">Degree</p>
              <p className="text-[15px] text-soft-ink">M.Sc. AI · DCU</p>
            </div>
            <div>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-soft-faint mb-1">Based in</p>
              <p className="text-[15px] text-soft-ink">Dublin, Ireland</p>
            </div>
          </div>
        </div>

         {/*Latest writing*/}
        {latestPost && (
          <div className="pt-8 pb-12">
            <div className="flex justify-between items-center mb-4 border-t border-soft-rule pt-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.6px] text-soft-faint">Latest writing</span>
              <Link to="/blog" className="text-sm text-soft-dim hover:text-soft-ink transition-colors">All posts →</Link>
            </div>
            <Link to={`/blog/${latestPost.slug}`} className="block cursor-pointer group">
              <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_160px] gap-4 sm:gap-8 items-start">
                <span className="font-mono text-[12px] text-soft-dim">
                  {latestPost.published_at ? new Date(latestPost.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                </span>
                <div>
                  <h2 className="text-[22px] font-medium leading-[1.2] tracking-[-0.5px] text-soft-ink group-hover:text-soft-dim transition-colors mb-1">
                    {latestPost.title}
                  </h2>
                  {latestPost.excerpt && (
                    <p className="text-[15px] text-soft-dim line-clamp-2">{latestPost.excerpt}</p>
                  )}
                </div>
                <span className="font-mono text-[12px] text-soft-faint text-right">
                  {latestPost.read_time ? `${latestPost.read_time} min` : ''}
                  {latestPost.tag_list?.length > 0 ? ` · ${latestPost.tag_list[0]}` : ''}
                </span>
              </div>
            </Link>
          </div>
        )}

        {/* Elsewhere */}
        <div className="pt-6 border-t border-soft-rule flex justify-between items-center">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-soft-faint">Elsewhere</span>
          <div className="flex gap-6">
            <a href="https://github.com/tabspacecoder" target="_blank" rel="noopener noreferrer" className="text-[14px] text-soft-ink hover:text-soft-dim border-b border-soft-rule pb-0.5 transition-colors">GitHub</a>
            <a href="https://linkedin.com/in/mugunthjc" target="_blank" rel="noopener noreferrer" className="text-[14px] text-soft-ink hover:text-soft-dim border-b border-soft-rule pb-0.5 transition-colors">LinkedIn</a>
            <a href="mailto:jc.mugunth@gmail.com" className="text-[14px] text-soft-ink hover:text-soft-dim border-b border-soft-rule pb-0.5 transition-colors">Email</a>
          </div>
        </div>
      </div>

      {/* RIGHT rail */}
      <aside>
        <RailBlock eyebrow="Currently" top>
          <p className="text-[17px] font-medium text-soft-ink mb-1">Working on Dwight AI</p>
          <p className="text-[14px] text-soft-dim mb-4">A PEFT model, that talks like Dwight K. Shrute from the office!</p>
          <div className="w-full h-[3px] bg-soft-rule mb-3">
            <div className="h-full bg-soft-ink" style={{ width: '75%' }} />
          </div>
          {/*<div className="flex justify-end">*/}
          {/*  /!*<span className="font-mono text-[11px] text-soft-faint">WEEK 8 / 12</span>*!/*/}
          {/*  <span className="font-mono text-[11px] text-soft-faint">SUBMITED · AUG 3</span>*/}
          {/*</div>*/}
        </RailBlock>

        <RailBlock eyebrow="On this site">
          <SideRow label="Work" to="/experience" />
          <SideRow label="Education" to="/education" />
          <SideRow label="Skills" to="/skills" />
          <SideRow label="Projects" to="/projects" />
          <SideRow label="Writing" to="/blog" />
        </RailBlock>

        <AboutSiteTrigger borderTop={false} />
      </aside>
    </div>
  )
}

export default HomePage
